import React, { useState } from 'react';

export interface LandlordInfoCardProps {
  data: {
    landlordName: string;
    landlordEmail: string;
    landlordPhone: string;
    landlordShare: number;
  };
}

interface InfoFieldProps {
  label: string;
  value: string;
  copyValue?: string;
  copiedKey?: string;
  onCopy?: (key: string, value: string) => void;
  className?: string;
}

const InfoField = ({
  label,
  value,
  copyValue,
  copiedKey,
  onCopy,
  className = ''
}: InfoFieldProps) => {
  const displayValue = value || '—';
  const canCopy = Boolean(copyValue);
  const isCopied = copiedKey === label;

  return (
    <div className={`rounded-lg border border-gray-100 bg-white/80 p-3 min-w-0 ${className}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-sm font-gilroy-medium text-neutral-500 uppercase tracking-wide">
          {label}
        </span>
        {canCopy && onCopy && (
          <button
            type="button"
            onClick={() => onCopy(label, copyValue || '')}
            className="shrink-0 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-gilroy-bold text-gray-500 transition-colors hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
          >
            {isCopied ? 'Copied' : 'Copy'}
          </button>
        )}
      </div>
      <div className="text-xl font-gilroy-bold text-gray-800 truncate" title={displayValue}>
        {displayValue}
      </div>
    </div>
  );
};

export const LandlordInfoCard: React.FC<LandlordInfoCardProps> = ({ data }) => {
  const [copiedKey, setCopiedKey] = useState<string>();

  const copyField = (key: string, value: string) => {
    if (!value) return;

    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(undefined), 1600);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm bg-gradient-to-b from-emerald-50/70 to-white p-5">
      <div className="flex items-start justify-between gap-3 mb-4 pb-3 border-b border-gray-100">
        <div className="min-w-0">
          <p className="text-sm text-emerald-600 font-gilroy-bold leading-tight uppercase tracking-wide">
            Homeowner details
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <InfoField
          label="Name"
          value={data.landlordName}
          copyValue={data.landlordName}
          copiedKey={copiedKey}
          onCopy={copyField}
        />
        <InfoField
          label="Phone"
          value={data.landlordPhone}
          copyValue={data.landlordPhone}
          copiedKey={copiedKey}
          onCopy={copyField}
        />
        <InfoField
          label="Email"
          value={data.landlordEmail}
          copyValue={data.landlordEmail}
          copiedKey={copiedKey}
          onCopy={copyField}
          className="md:col-span-2"
        />
      </div>
    </div>
  );
};
